/**
 *  @class SoulogMobileConnect
 *
 *  "Connect" radial ring + 3D parallax (mobile only). Composition and motion parameters are
 *  ported from the balanced preset in tmp/parallax-3d-demo.html, with the tuning panel removed.
 *
 *  Geometry is hard-coded here rather than exposed in the theme editor: x/y/w/z are part of the
 *  composition (10 images spaced evenly at 36° around an elliptical ring, each then offset by its
 *  own radius factor). Changing one means re-laying out the whole ring, so it is not a knob a
 *  merchant should turn.
 */
if (!customElements.get("soulog-mobile-connect")) {
	// x/y = center point (% of stage width/height), w = width (% of stage width), z = depth (positive = floats in front of the product)
	// fx/fy/fr/dur/delay = float amplitude, period and phase
	const ITEMS = [
		{ x: 42.8, y: 19.7, w: 21.2, rot: -2, z: -60, fx: -6, fy: 9, fr: 0.8, dur: 8.9, delay: -1.2 },
		{ x: 69.5, y: 23.8, w: 14.9, rot: 2, z: -160, fx: 5, fy: 10, fr: -0.9, dur: 8.1, delay: -2.8 },
		{ x: 86, y: 39.2, w: 16, rot: 2.5, z: -140, fx: 6, fy: 7, fr: -0.5, dur: 9.4, delay: -1.7 },
		{ x: 81.5, y: 52.7, w: 19.4, rot: 1.5, z: 60, fx: -5, fy: 8, fr: -0.6, dur: 8.4, delay: -2.1 },
		{ x: 86.6, y: 65.4, w: 17.2, rot: -1.5, z: -100, fx: 7, fy: 6, fr: -0.7, dur: 7.8, delay: -4.4 },
		{ x: 55.3, y: 75.2, w: 28.6, rot: 0.5, z: 90, fx: 6, fy: -7, fr: 0.4, dur: 9.2, delay: -3.6 },
		{ x: 22.6, y: 74.5, w: 19.7, rot: -1, z: 45, fx: -4, fy: -8, fr: 0.5, dur: 9.6, delay: -5.2 },
		{ x: 14.5, y: 60.9, w: 15, rot: 1.5, z: -180, fx: -5, fy: -7, fr: 0.6, dur: 10.2, delay: -6.1 },
		{ x: 13.5, y: 45, w: 17, rot: -2.5, z: -110, fx: -7, fy: 8, fr: 0.9, dur: 11, delay: -7.3 },
		{ x: 17.5, y: 29.7, w: 17.8, rot: -1.5, z: 50, fx: 4, fy: -9, fr: 0.7, dur: 7.5, delay: -0.4 },
	];
	const PRODUCT = { x: 50, y: 50, w: 30, rot: 0, z: 0, fx: 0, fy: -3, fr: 0, dur: 12, delay: 0 };

	// Shadows are tiered by depth: the closer an item, the larger and darker its shadow (it floats higher); distant items still get a faint shadow
	const shadowFor = (z, isProduct) => {
		if (isProduct) return { y: 30, b: 54, a: 0.2 };
		if (z >= 60) return { y: 24, b: 44, a: 0.18 };
		if (z >= 0) return { y: 19, b: 36, a: 0.15 };
		if (z >= -100) return { y: 13, b: 27, a: 0.12 };
		return { y: 9, b: 20, a: 0.09 };
	};

	// Fixed values of the balanced preset (the "balanced" tier in the demo's #presets; the tuning panel was removed, so this is the only form)
	const P = { cam: 1, speed: 1, tilt: 2, depth: 1, scroll: 1, touch: 1 };
	const TOUCH_EASE = 0.1;
	const STAGE_AR = 575 / 1000; // stage width/height, used to convert between "% of width" and "% of height"

	// Two incommensurable sines summed; the periods do not divide evenly -> the path does not repeat for a long time. A single sin reveals its mechanical loop after a few cycles
	const wobbleX = (t) => Math.sin(t * 0.21) * 0.62 + Math.sin(t * 0.13 + 1.3) * 0.38;
	const wobbleY = (t) => Math.cos(t * 0.17) * 0.62 + Math.sin(t * 0.11 + 2.1) * 0.38;

	const GROW = { hold: 1.8, step: 0.08, dur: 0.28 }; // hold includes the 1.7s image entrance

	class SoulogMobileConnect extends HTMLElement {
		connectedCallback() {
			this.stage = this.querySelector(".smn__stage");
			this.world = this.querySelector(".smn__world");
			this.canvas = this.querySelector(".smn__wires");
			if (!this.stage || !this.world || !this.canvas) return;

			this.lineColor = this.dataset.lineColor || "#FF6301";

			this.nodes = Array.from(this.querySelectorAll(".smn__item")).map((el) => {
				const idx = el.dataset.ringIndex;
				const cfg = idx === "product" ? PRODUCT : ITEMS[parseInt(idx, 10) - 1];
				return { el, cfg, isProduct: idx === "product" };
			});
			this.productIndex = this.nodes.findIndex((n) => n.isProduct);

			this.applyGeometry();
			this.applyEnterOrder();
			this.buildWireNetwork();

			this.reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

			if (new URLSearchParams(window.location.search).get("smn-debug") === "1") {
				this.nodes.forEach((n) => n.el.classList.add("is-in"));
				this.report();
				return;
			}

			if (this.reduced) {
				this.nodes.forEach((n) => n.el.classList.add("is-in"));
				this.resize();
				// The wires must be drawn in their "fully grown" state. drawWires has a growth guard (it returns
				// early if growT0 is null or T has not yet passed GROW.hold), so we cannot simply call drawWires(0) --
				// that would show reduced-motion users a "Connect" with not a single wire, dropping exactly what
				// this block is meant to convey: "fragments being strung together". Passing a T far beyond the
				// growth period clamps every edge's progress to 1.
				this.growT0 = 0;
				this.drawWires(GROW.hold + 1000);
				// The wires are already drawn, so the copy should stay on the "after connecting" version
				this.classList.add("is-connected");
				return;
			}

			this.resize = this.resize.bind(this);
			this.resize();
			window.addEventListener("resize", this.resize);

			// rAF only runs while the section is on or near the screen (same approach as assets/cards-fan.js)
			this.io = new IntersectionObserver(
				(entries) => entries.forEach((e) => (e.isIntersecting ? this.start() : this.stop())),
				{ rootMargin: "200% 0px 200% 0px" },
			);
			this.io.observe(this);

			// Entrance: wait until this block is **mostly in view** before triggering -- the wires are the climax of
			// this section; if they start growing when only half of it is showing, they are already done by the
			// time the user scrolls into place, so effectively nobody sees them.
			//
			// The threshold cannot be hard-coded to 1: this block (copy + stage) is often taller than a phone
			// viewport, in which case the IO would never fire. So it is derived from "what fraction of this block
			// the viewport can hold", with an 8% margin; the 0.95 cap avoids getting stuck on sub-pixel error,
			// and the 0.35 floor ensures it still fires on very short viewports.
			const visibleRatio = window.innerHeight / Math.max(1, this.offsetHeight);
			const enterThreshold = Math.min(0.95, Math.max(0.35, visibleRatio * 0.92));

			this.enterIo = new IntersectionObserver(
				(entries) => {
					entries.forEach((e) => {
						if (!e.isIntersecting || this.growT0 != null) return;
						this.nodes.forEach((n) => n.el.classList.add("is-in"));
						this.growT0 = performance.now() / 1000; // wires start growing only after the images have entered
					});
				},
				{ threshold: enterThreshold },
			);
			this.enterIo.observe(this);

			// Re-arm after fully leaving the viewport, so scrolling back replays the full "fragments -> wires" sequence.
			// Uses a separate threshold:0 observer; the one above cannot be reused -- its threshold is high (it only
			// fires once the block is "fully in view"), so any slight scroll drops the visible ratio below it,
			// isIntersecting turns false, and the animation would reset while the user is still watching.
			this.resetIo = new IntersectionObserver((entries) => entries.forEach((e) => !e.isIntersecting && this.rearm()), {
				threshold: 0,
			});
			this.resetIo.observe(this);

			this.onFinger = this.onFinger.bind(this);
			this.release = this.release.bind(this);
			this.stage.addEventListener("pointermove", this.onFinger, { passive: true });
			this.stage.addEventListener("pointerdown", this.onFinger, { passive: true });
			this.stage.addEventListener("pointerup", this.release);
			this.stage.addEventListener("pointercancel", this.release);
			this.stage.addEventListener("pointerleave", this.release);
		}

		disconnectedCallback() {
			this.stop();
			window.removeEventListener("resize", this.resize);
			if (this.io) this.io.disconnect();
			if (this.enterIo) this.enterIo.disconnect();
			if (this.resetIo) this.resetIo.disconnect();
		}

		/** Revert to the unplayed state: items retract, wires are erased, copy returns to "Capture"; replays on the next viewport entry */
		rearm() {
			if (this.growT0 == null) return; // nothing to reset if it has not played yet
			this.growT0 = null;
			this.connected = false;
			this.classList.remove("is-connected");
			this.nodes.forEach((n) => n.el.classList.remove("is-in"));
			this.drawWires(0); // growT0 is already null, so this call only clears the canvas
		}

		/** Write the ITEMS geometry as CSS custom properties -- CSS computes positions, JS only handles motion */
		applyGeometry() {
			this.nodes.forEach(({ el, cfg, isProduct }) => {
				el.style.setProperty("--x", cfg.x);
				el.style.setProperty("--y", cfg.y);
				el.style.setProperty("--w", cfg.w);
				el.style.setProperty("--z", cfg.z);
				const sh = shadowFor(cfg.z, isProduct);
				const img = el.querySelector("img, div");
				if (!img) return;
				if (isProduct) {
					// The product is a PNG with transparent margins (measured: in 560x840 the device only spans x61..507, with 11% fully transparent all around).
					// box-shadow traces the element's rectangular box and ignores alpha, so it draws a dark halo around that
					// invisible rectangle -- the middle keeps the page background while the surroundings darken, making the device look like it sits on a white card.
					// drop-shadow follows the alpha channel, so it hugs the device outline.
					// The other ring items are opaque photos/white cards whose rectangular box is the outline, so they keep the cheaper box-shadow.
					img.style.filter = `drop-shadow(0 ${sh.y}px ${sh.b}px rgba(0,0,0,${sh.a}))`;
				} else {
					img.style.boxShadow = `0 ${sh.y}px ${sh.b}px rgba(0,0,0,${sh.a})`;
				}
			});
		}

		/** Entrance order: the product settles first, then items appear one by one from near to far */
		applyEnterOrder() {
			const order = [...this.nodes.keys()].sort((a, b) => {
				if (this.nodes[a].isProduct) return -1;
				if (this.nodes[b].isProduct) return 1;
				return this.nodes[b].cfg.z - this.nodes[a].cfg.z;
			});
			order.forEach((nodeIdx, rank) => {
				this.nodes[nodeIdx].el.querySelector(".smn__enter")?.style.setProperty("--smn-in-delay", `${rank * 0.08}s`);
			});
		}

		/**
		 * Wire network (irregular organic mesh) -- ported as-is from the demo: the 10 images are split into 6 groups of
		 * ring neighbors, each group sharing one hub; each hub's angle/radius/depth gets a deterministic random jitter
		 * (fixed seed = reproducible), so they are scattered irregularly at varying depths. Hubs are not DOM elements;
		 * their positions are computed by project(), which reproduces the CSS transforms.
		 */
		buildWireNetwork() {
			const items = this.nodes.filter((n) => !n.isProduct).map((n) => n.cfg);
			const product = PRODUCT;

			// Support radius of the product rectangle along direction th (% of width) -- hubs must land outside it
			const prodR = (th) => {
				const w = product.w,
					hw = w / 0.67;
				const c = Math.abs(Math.cos(th)),
					s = Math.abs(Math.sin(th));
				return Math.min(c < 1e-6 ? 1e9 : w / 2 / c, s < 1e-6 ? 1e9 : hw / 2 / s);
			};

			// Angle of each image relative to the product (the order ITEMS is written in is itself the ring order)
			const nodeTh = items.map((it) => Math.atan2((it.y - 50) / STAGE_AR, it.x - 50));

			// Deterministic pseudo-random (LCG): the layout is scrambled, but identical on every refresh and verifiable offline
			let rndSeed = 1;
			const rnd = () => {
				rndSeed = (rndSeed * 1103515245 + 12345) & 0x7fffffff;
				return rndSeed / 0x7fffffff;
			};

			// Adjacent images are split into 6 groups, each sharing one hub (a hub is a shared junction, not one per image)
			const GROUPS = [[0, 1], [2, 3], [4, 5], [6], [7, 8], [9]];
			const HUBLINK = [
				[0, 1],
				[2, 3],
				[4, 5],
			]; // a few diagonal hub-to-hub links to add mesh texture

			const JUNC = GROUPS.map((g) => {
				// Group angle uses a vector average (a scalar average would fling the hub to the opposite side when crossing ±180°)
				const ta = Math.atan2(
					g.reduce((a, i) => a + Math.sin(nodeTh[i]), 0),
					g.reduce((a, i) => a + Math.cos(nodeTh[i]), 0),
				);
				const t = ta + (rnd() - 0.5) * 0.42; // ±12° angle jitter
				const r = prodR(t) + 3 + rnd() * 7; // random depth within the corridor
				return { x: 50 + r * Math.cos(t), y: 50 + r * Math.sin(t) * STAGE_AR, z: -10 - rnd() * 30 };
			});

			// PRODUCT's index in PTS is fixed at items.length (the end), unrelated to this.productIndex --
			// the wire network only cares about the set of geometric points, not DOM order.
			const PRODUCT_PT = items.length;
			const J0 = items.length + 1;

			/* Three kinds of edges:
			   - spokes          -- product -> hub -> image, so "the product is the central junction" reads clearly (drawn solid)
			   - neighbor links  -- between adjacent images in different groups ("adjacent photos are connected too", drawn faint)
			   - hub diagonals   -- a few hub-to-hub links for texture (drawn faint) */
			const EDGES = [];
			const SPOKE = [];
			GROUPS.forEach((g, hi) => {
				EDGES.push([PRODUCT_PT, J0 + hi]);
				SPOKE.push(true);
				g.forEach((i) => {
					EDGES.push([J0 + hi, i]);
					SPOKE.push(true);
				});
			});
			for (let i = 0; i < items.length; i++) {
				const j = (i + 1) % items.length;
				const gi = GROUPS.findIndex((g) => g.includes(i));
				const gj = GROUPS.findIndex((g) => g.includes(j));
				if (gi !== gj) {
					EDGES.push([i, j]);
					SPOKE.push(false);
				} // same-group images already share a hub; only link across groups
			}
			HUBLINK.forEach(([a, b]) => {
				EDGES.push([J0 + a, J0 + b]);
				SPOKE.push(false);
			});

			const PTS = items
				.map((it) => ({ x: it.x, y: it.y, z: it.z }))
				.concat([{ x: product.x, y: product.y, z: product.z }], JUNC);

			const adj = PTS.map(() => []);
			EDGES.forEach(([a, b]) => {
				adj[a].push(b);
				adj[b].push(a);
			});

			// BFS hop count from the product -- determines the order in which wires grow
			const hop = PTS.map(() => Infinity);
			hop[PRODUCT_PT] = 0;
			const bfsQ = [PRODUCT_PT];
			while (bfsQ.length) {
				const c = bfsQ.shift();
				for (const n of adj[c])
					if (hop[n] > hop[c] + 1) {
						hop[n] = hop[c] + 1;
						bfsQ.push(n);
					}
			}
			const edgeHop = EDGES.map(([a, b]) => Math.min(hop[a], hop[b]));
			const toProduct = EDGES.map(([a, b]) => a === PRODUCT_PT || b === PRODUCT_PT);
			const flows = EDGES.map((_, i) => ({ t: (i * 0.37) % 1, s: 0.0018 + (i % 5) * 0.0004 }));

			this.wireNet = { PTS, EDGES, SPOKE, hop, edgeHop, toProduct, flows, J0, PRODUCT_PT };
		}

		resize() {
			const r = this.stage.getBoundingClientRect();
			this.stageW = r.width || 390;
			// The stage is already a 575:1000 fixed-ratio box (aspect-ratio CSS), so r.height normally has a value;
			// the STAGE_AR conversion is only a fallback, STAGE_AR = width/height
			this.stageH = r.height || this.stageW / STAGE_AR;
			this.persPx = Math.max(700, Math.min(1400, this.stageW * 2.3));
			this.style.setProperty("--smn-pers", this.persPx.toFixed(0));
			const dpr = Math.min(2, window.devicePixelRatio || 1);
			this.canvas.width = Math.round(this.stageW * dpr);
			this.canvas.height = Math.round(this.stageH * dpr);
			this.ctx = this.canvas.getContext("2d");
			this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
			this.camPx = this.camPx || { x: 0, y: 0 };
			this.rotDeg = this.rotDeg || { x: 0, y: 0 };
			this.touchTgt = this.touchTgt || { x: 0, y: 0 };
			this.touchCur = this.touchCur || { x: 0, y: 0 };
			this.scrollProgress = this.scrollProgress || 0;
		}

		/** Reproduce the CSS 3D transform chain in JS to project points back to canvas 2D coordinates (for wire endpoints) */
		project(p) {
			const cx = this.stageW / 2,
				cy = this.stageH / 2;
			const dx = (p.x / 100) * this.stageW - cx;
			const sy = this.scrollProgress * ((p.z / 4) * P.scroll);
			const dy = (p.y / 100) * this.stageH - cy + sy;
			const dz = p.z * P.depth;
			const ry = (this.rotDeg.y * Math.PI) / 180,
				rx = (this.rotDeg.x * Math.PI) / 180;
			const x1 = dx * Math.cos(ry) + dz * Math.sin(ry);
			const z1 = -dx * Math.sin(ry) + dz * Math.cos(ry);
			const y2 = dy * Math.cos(rx) - z1 * Math.sin(rx);
			const z2 = dy * Math.sin(rx) + z1 * Math.cos(rx);
			const ox = cx + this.camPx.x,
				oy = this.stageH * 0.48 + this.camPx.y;
			const k = this.persPx / (this.persPx - z2);
			return { x: ox + (cx + x1 - ox) * k, y: oy + (cy + y2 - oy) * k };
		}

		measureScroll() {
			const r = this.stage.getBoundingClientRect();
			const center = r.top + r.height / 2;
			this.scrollProgress = Math.max(
				-1,
				Math.min(1, (window.innerHeight / 2 - center) / (window.innerHeight / 2 + r.height / 2)),
			);
		}

		drawWires(T) {
			const ctx = this.ctx;
			if (!ctx) return;
			ctx.clearRect(0, 0, this.stageW, this.stageH);
			if (this.growT0 == null) return;
			const gt = T - this.growT0 - GROW.hold;
			if (gt <= 0) return;

			const { PTS, EDGES, SPOKE, hop, edgeHop, toProduct, flows, J0 } = this.wireNet;
			const P2 = PTS.map((p) => this.project(p));
			ctx.lineCap = "round";
			const col = this.lineColor;
			const rgb = this.lineRgb || (this.lineRgb = this.hexToRgb(col));

			EDGES.forEach((e, ei) => {
				const g = Math.min(1, (gt - edgeHop[ei] * GROW.step) / GROW.dur);
				if (g <= 0) return;
				const a = P2[e[0]],
					b = P2[e[1]];
				const spoke = SPOKE[ei];
				const ex = b.x - a.x,
					ey = b.y - a.y,
					L = Math.hypot(ex, ey) || 1;
				// Spokes stay straight (pointing straight at the product is what reads as "converging"); ring edges can bend more loosely
				const sway = Math.sin(T * 1.1 + ei * 0.7) * (spoke ? 1.6 : 5);
				const cpx = (a.x + b.x) / 2 + (-ey / L) * sway;
				const cpy = (a.y + b.y) / 2 + (ex / L) * sway;

				// Only draw the [0,g] segment: a sub-curve of a quadratic Bezier can be split out exactly (De Casteljau)
				const u = 1 - g;
				ctx.beginPath();
				ctx.moveTo(a.x, a.y);
				ctx.quadraticCurveTo(
					a.x + (cpx - a.x) * g,
					a.y + (cpy - a.y) * g,
					u * u * a.x + 2 * u * g * cpx + g * g * b.x,
					u * u * a.y + 2 * u * g * cpy + g * g * b.y,
				);
				ctx.strokeStyle = spoke ? `rgba(${rgb},0.42)` : `rgba(${rgb},0.22)`;
				ctx.lineWidth = spoke ? 1.4 : 1;
				ctx.stroke();

				// Energy dots only run along the product edges, and flow "from outside in" -- converging, not diverging
				if (g >= 1 && toProduct[ei]) {
					const f = flows[ei];
					f.t = (f.t + f.s) % 1;
					const t = 1 - f.t,
						v = f.t;
					const px = v * v * a.x + 2 * v * t * cpx + t * t * b.x;
					const py = v * v * a.y + 2 * v * t * cpy + t * t * b.y;
					const r = 2;
					const gr = ctx.createRadialGradient(px, py, 0, px, py, r * 3);
					gr.addColorStop(0, `rgba(${rgb},0.85)`);
					gr.addColorStop(1, `rgba(${rgb},0)`);
					ctx.fillStyle = gr;
					ctx.beginPath();
					ctx.arc(px, py, r * 3, 0, 7);
					ctx.fill();
					ctx.fillStyle = col;
					ctx.beginPath();
					ctx.arc(px, py, r * 0.85, 0, 7);
					ctx.fill();
				}
			});

			// Hub glow dots: light up together with their own BFS hop count
			for (let i = J0; i < PTS.length; i++) {
				const g = Math.min(1, (gt - hop[i] * GROW.step) / GROW.dur);
				if (g <= 0) continue;
				const p = P2[i];
				const R = 6.5 * (1 + Math.sin(T * 1.6 + i) * 0.14) * g;
				const halo = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, R);
				halo.addColorStop(0, `rgba(${rgb},0.85)`);
				halo.addColorStop(0.35, `rgba(${rgb},0.45)`);
				halo.addColorStop(1, `rgba(${rgb},0)`);
				ctx.fillStyle = halo;
				ctx.beginPath();
				ctx.arc(p.x, p.y, R, 0, 7);
				ctx.fill();
				ctx.fillStyle = `rgba(255,244,236,${g})`;
				ctx.beginPath();
				ctx.arc(p.x, p.y, 1.5, 0, 7);
				ctx.fill();
			}
		}

		hexToRgb(hex) {
			const h = hex.replace("#", "");
			const n = parseInt(h.length === 3 ? h.replace(/(.)/g, "$1$1") : h, 16);
			return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
		}

		frame(now) {
			if (!this.active) return;

			const dt = Math.min(0.05, (now - (this.t0 ?? now)) / 1000);
			this.t0 = now;
			this.phase = (this.phase ?? 0) + dt * P.speed;

			this.measureScroll();

			// Finger follow: ease smoothly toward the finger target; on release touchTgt resets to zero and it drifts back to center
			this.touchCur.x += (this.touchTgt.x - this.touchCur.x) * TOUCH_EASE;
			this.touchCur.y += (this.touchTgt.y - this.touchCur.y) * TOUCH_EASE;

			// Camera pan: autopilot base drift + finger offset, zero distortion, all items stay front-facing
			const camAmp = this.stageW * 0.08 * P.cam;
			const tAmp = this.stageW * 0.09 * P.touch;
			const camLim = this.stageW * 0.13;
			const clamp = (v) => Math.max(-camLim, Math.min(camLim, v));
			this.camPx = {
				x: clamp(wobbleX(this.phase) * camAmp + this.touchCur.x * tAmp),
				y: clamp(wobbleY(this.phase) * camAmp * 0.7 + this.touchCur.y * tAmp),
			};
			this.style.setProperty("--smn-cam-x", this.camPx.x.toFixed(2) + "px");
			this.style.setProperty("--smn-cam-y", this.camPx.y.toFixed(2) + "px");

			// View rotation: autopilot + finger offset; the product stays front-facing by counter-applying rotateY/rotateX in reverse order
			this.rotDeg = {
				x: -wobbleY(this.phase) * P.tilt - this.touchCur.y * 5 * P.touch,
				y: wobbleX(this.phase) * P.tilt + this.touchCur.x * 5 * P.touch,
			};
			this.world.style.setProperty("--smn-rx", this.rotDeg.x.toFixed(3) + "deg");
			this.world.style.setProperty("--smn-ry", this.rotDeg.y.toFixed(3) + "deg");

			this.nodes.forEach(({ el, cfg }) => {
				el.style.setProperty("--sy", (this.scrollProgress * (cfg.z / 4) * P.scroll).toFixed(2));
			});

			// The copy follows the visuals: the moment the first wire starts growing, it cross-fades from "Capture" to "Connect".
			// The check uses the same clock and the same expression as the growth guard in drawWires, so the two never fall out of sync.
			if (!this.connected && this.growT0 != null && now / 1000 - this.growT0 - GROW.hold > 0) {
				this.connected = true;
				this.classList.add("is-connected");
			}

			// Only touch the canvas after all styles are written -- no interleaved reads/writes, avoiding forced synchronous layout
			this.drawWires(now / 1000);
			this.raf = requestAnimationFrame((t) => this.frame(t));
		}

		start() {
			if (this.active || this.reduced) return;
			this.active = true;
			this.t0 = performance.now();
			this.raf = requestAnimationFrame((t) => this.frame(t));
		}

		stop() {
			this.active = false;
			if (this.raf) {
				cancelAnimationFrame(this.raf);
				this.raf = null;
			}
		}

		/* Finger follow: passive listeners, no preventDefault: while the finger swipes, the page scrolls as usual and the
		   scene additionally follows along. touch-action: pan-y (see .smn__stage) ensures horizontal pointermove is not
		   swallowed by phone gestures, so left/right can keep up. On lift, release resets to zero and the scene eases back. */
		onFinger(e) {
			const r = this.stage.getBoundingClientRect();
			this.touchTgt.x = Math.max(-1, Math.min(1, (e.clientX - (r.left + r.width / 2)) / (r.width / 2)));
			this.touchTgt.y = Math.max(-1, Math.min(1, (e.clientY - (r.top + r.height / 2)) / (r.height / 2)));
		}

		release() {
			this.touchTgt.x = 0;
			this.touchTgt.y = 0;
		}

		report() {
			const SAFE = 20;
			const fails = [];
			const stageBox = this.stage.getBoundingClientRect();
			this.nodes.forEach(({ el }, i) => {
				const b = el.getBoundingClientRect();
				if (b.left < stageBox.left + SAFE - 0.5) fails.push(`item${i + 1}-left`);
				if (b.right > stageBox.right - SAFE + 0.5) fails.push(`item${i + 1}-right`);
			});
			const de = document.documentElement;
			if (de.scrollWidth > de.clientWidth + 1) fails.push(`h-overflow(${de.scrollWidth - de.clientWidth}px)`);
			de.dataset.smnVerdict = fails.length ? `FAIL:${fails.join(",")}` : "PASS";
		}
	}

	customElements.define("soulog-mobile-connect", SoulogMobileConnect);
}
